import { featureEqualityUtil } from 'utils/geoJSONFeatureUtils/featureEqualityUtil';

import { GEOJSON_PROPERTY } from 'components/Map/hooks/useFeatures/constants';

import { MAP_ACTION_TYPE } from './constants';
import type { CircleUpdatePointsAction, MapAction, PolygonUpdatePointsAction } from './types';

const { isCoordinatesEqual, isFeatureEqual, isNumberEqualWithinTolerance } = featureEqualityUtil;

const isCircleUpdatePointsAction = (action: MapAction): action is CircleUpdatePointsAction =>
    action.type === MAP_ACTION_TYPE.CIRCLE_UPDATE_POINTS;

const isCircleUpdatePointsActionEqual = (action1: MapAction, action2: MapAction): boolean => {
    if (!isCircleUpdatePointsAction(action1) || !isCircleUpdatePointsAction(action2)) {
        return false;
    }

    const { circle: circle1 } = action1;
    const { circle: circle2 } = action2;

    const isSameCenterPoint = isCoordinatesEqual(
        circle1.geometry.coordinates[0][0],
        circle2.geometry.coordinates[0][0]
    );

    const HALF_METER_TOLERANCE = 0.5;
    const isSameRadius = isNumberEqualWithinTolerance(
        circle1.properties?.[GEOJSON_PROPERTY.RADIUS_METERS],
        circle2.properties?.[GEOJSON_PROPERTY.RADIUS_METERS],
        HALF_METER_TOLERANCE
    );

    return isSameCenterPoint && isSameRadius;
};

const isPolygonUpdatePointsAction = (action: MapAction): action is PolygonUpdatePointsAction =>
    action.type === MAP_ACTION_TYPE.POLYGON_UPDATE_POINTS;

const isPolygonUpdatePointsActionEqual = (action1: MapAction, action2: MapAction): boolean => {
    if (!isPolygonUpdatePointsAction(action1) || !isPolygonUpdatePointsAction(action2)) {
        return false;
    }

    return isFeatureEqual(action1.polygon, action2.polygon);
};

const ACTION_EQUALITY_MAP: Record<
    MAP_ACTION_TYPE,
    (action1: MapAction, action2: MapAction) => boolean
> = {
    [MAP_ACTION_TYPE.CIRCLE_UPDATE_POINTS]: isCircleUpdatePointsActionEqual,
    [MAP_ACTION_TYPE.POLYGON_UPDATE_POINTS]: isPolygonUpdatePointsActionEqual
};

const isSameAction = (action1: MapAction, action2: MapAction): boolean => {
    if (action1.type !== action2.type) {
        return false;
    }

    return ACTION_EQUALITY_MAP[action1.type](action1, action2);
};

export default isSameAction;
